import Accordion from '@mui/material/Accordion'
import AccordionDetails from '@mui/material/AccordionDetails'
import AccordionSummary from '@mui/material/AccordionSummary'
import Box from '@mui/material/Box'
import Divider from '@mui/material/Divider'
import { useTheme } from '@mui/material/styles'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import Grid from '@mui/material/Grid2'
import { useTranslation } from 'react-i18next'
import { IEmployer, IPersonRole } from 'types/cvsInterfaces'
import { IItemVisibility, ILayout } from 'types/layoutInterfaces'
import { chooseDBTranslation } from 'utils/translations'
import { convertToDisplayDate } from 'utils/utils'
import VisibilityCheckbox from '../VisibilityCheckbox'
import HideDetailsSettings from './HideDetailsSettings'
import { findHideSetting, hasHiddenDetails } from '../hideSettings'
import colors from 'constants/colors'
import React from 'react'

interface WorkHistoryProps {
  employments: IEmployer[]
  layout: ILayout
  hideSection: (value: boolean, type: string) => void
  hideItem: (value: boolean, id: number, type: string) => void
  expanded: boolean
  setStatus: React.Dispatch<React.SetStateAction<boolean>>
  handleHideSettings: (data: Record<string, boolean>, type: string) => void
}

/**
 * Renders the work history section of the component.
 *
 * @param {Object} employments - The list of employments.
 * @param {Object} layout - The layout configuration.
 * @param {boolean} hideSection - Function to hide the section.
 * @param {boolean} hideItem - Function to hide an item.
 * @param {boolean} expanded - Flag indicating whether the section is expanded.
 * @param {function} setStatus - Function to set the status of the section.
 * @return {React.ReactElement} The rendered work history section.
 * @notExported
 */
const WorkHistory: React.FC<WorkHistoryProps> = ({
  employments,
  layout,
  hideSection,
  hideItem,
  expanded,
  setStatus,
  handleHideSettings,
}) => {
  const { t, i18n } = useTranslation()
  const theme = useTheme()

  const hideSetting = findHideSetting(layout, 'employments')

  let hidden = false
  if (layout && layout.ItemVisibilities && layout.ItemVisibilities.length > 0) {
    const visibilities = layout.ItemVisibilities.filter(
      (visibility: IItemVisibility) => visibility.type === 'employments'
    )
    if (visibilities.length > 0) {
      const visibilityMap = new Map(visibilities.map(item => [item.itemId, item.hidden]))
      hidden = employments.every(value => visibilityMap.get(value.id) === true)
    }
  }

  const Item: React.FC<{ employment: IEmployer }> = ({ employment }) => {
    let hiddenItem = false
    if (layout && layout.ItemVisibilities && layout.ItemVisibilities.length > 0) {
      const visibilities = layout.ItemVisibilities.filter(
        (visibility: IItemVisibility) => visibility.type === 'employments'
      )
      if (visibilities.length > 0) {
        hiddenItem = visibilities.find(
          (value: IItemVisibility) => value.itemId === employment.id && value.hidden === true
        )
          ? true
          : false
      }
    }

    return (
      <Box
        display="flex"
        flexDirection="row"
        flexWrap="nowrap"
        width="100%"
        my={2}
        sx={{
          padding: '2px',
          backgroundColor: theme.palette.background.default,
          '&:hover': {
            backgroundColor: theme.palette.background.paper,
            borderRadius: '5px',
          },
        }}
      >
        <Box width="100%">
          <Box display="flex" flexDirection="row" flexWrap="nowrap" width="100%" mb={1}>
            <VisibilityCheckbox
              checked={!!hiddenItem}
              onChange={(checked: boolean) => hideItem(checked, employment.id, 'employments')}
            />
            <Box fontWeight="fontWeightBold" flexGrow={1}>
              {chooseDBTranslation(i18n, employment).name}
            </Box>
            <Box justifyContent="flex-end">
              {convertToDisplayDate(employment.startDate, employment.dateResolution)} -{' '}
              {employment.endDate ? convertToDisplayDate(employment.endDate, employment.dateResolution) : null}
            </Box>
          </Box>
          <Box display="flex" flexDirection="row" flexWrap="nowrap" width="100%" mb={1}>
            <Box mr={1} fontWeight="fontWeightBold">
              {t('print.employment.roles')}:
            </Box>
            <Box display="inline">
              {chooseDBTranslation(i18n, employment).primaryRole.length > 0
                ? chooseDBTranslation(i18n, employment).primaryRole
                : employment.EmployerRoles?.map(
                    (role: IPersonRole) => `${chooseDBTranslation(i18n, role.Skill).name} `
                  )}
            </Box>
          </Box>
          <Box>{chooseDBTranslation(i18n, employment).description}</Box>
        </Box>
      </Box>
    )
  }

  return (
    <Accordion
      expanded={expanded}
      onChange={(_e, expanded) => setStatus(expanded)}
      elevation={0}
      sx={{ borderRadius: '5px', border: `1px solid ${colors.borderColor}` }}
      slotProps={{
        transition: { unmountOnExit: true },
      }}
    >
      <Box display="flex" alignItems="center">
        <VisibilityCheckbox checked={hidden} onChange={(checked: boolean) => hideSection(checked, 'employments')} />
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{
            flex: 1,
            fontSize: '20px',
            color: theme.palette.primary.main,
            fontWeight: 'bold',
          }}
        >
          <Grid container alignItems="center">
            <Grid>{t(`print.layout.workhistory`)}</Grid>
            <Grid sx={{ marginLeft: 1, fontSize: '11pt', color: theme.palette.text.secondary, fontWeight: 'normal' }}>
              {hasHiddenDetails(hideSetting, 'employments')
                ? t('print.hideSetting.enabled')
                : t('print.hideSetting.disabled')}
            </Grid>
          </Grid>
        </AccordionSummary>
      </Box>
      <AccordionDetails>
        <HideDetailsSettings
          type="employments"
          layout={layout}
          handleHideSettings={handleHideSettings}
          testIdPrefix="workhistory"
        />
        <Box display="flex" flexDirection="column" flexWrap="wrap" width="100%">
          {employments.map((employment: IEmployer, index: number) => {
            return (
              <Box key={`employment-${index}`}>
                <Item employment={employment} />
                {index + 1 < employments.length ? <Divider variant="fullWidth" /> : null}
              </Box>
            )
          })}
        </Box>
      </AccordionDetails>
    </Accordion>
  )
}

export default WorkHistory
